import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  AddShoppingCart as AddShoppingCartIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PartnerLayout from '../../components/partner/PartnerLayout';
import PartnerHelpPanel from '../../components/partner/PartnerHelpPanel';
import PurchaseCreditsDialog from '../../components/partner/PurchaseCreditsDialog';
import LimitRequestDialog from '../../components/partner/LimitRequestDialog';
import partnerApiService from '../../services/partnerApi';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';
import { CreditPackage, PartnerPackages } from '../../types/creditPackage';

// COMPRAR CRÉDITOS del partner. Cambio de modelo: el partner compra A GRANEL a
// un PRECIO MAYORISTA PLANO (el mismo por crédito compre 100 o 100.000) y NO
// revende «paquetes con margen». Los paquetes con descuento por volumen son la
// oferta al CLIENTE FINAL, así que aquí no se calcula coste ni margen por
// paquete (eso producía márgenes NEGATIVOS: el paquete grande se vende barato).
//   · Arriba: el cuadro de COMPRA a granel, con su precio plano y su cupo
//     mensual (cuánto ha comprado de su límite este mes).
//   · Abajo, SOLO como REFERENCIA de mercado: los precios de WAI al cliente
//     final (PVP), para que el partner sepa a cuánto se vende — nunca como su
//     coste ni con «tu margen».

const formatUsd = (value: number | string | null | undefined): string => {
  const num = Number(value ?? 0);
  return Number.isNaN(num) ? String(value ?? '') : num.toFixed(2);
};

const formatCredits = (value: number | string | null | undefined): string => {
  const num = Number(value ?? 0);
  return Number.isNaN(num) ? String(value ?? '') : num.toLocaleString();
};

const formatPct = (value: number | string | null | undefined): string => {
  const num = Number(value ?? 0);
  return Number.isNaN(num) ? String(value ?? '') : num.toFixed(1);
};

const PartnerPackagesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  // El descuento, el límite y lo comprado este ciclo salen de /partner/me (una
  // sola fuente): el diálogo de compra los necesita para importe y cupo.
  const { reseller, refreshMe } = usePartnerAuth();
  const [data, setData] = useState<PartnerPackages | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [buyOpen, setBuyOpen] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setData(await partnerApiService.getPackages());
    } catch (err: any) {
      console.error('Failed to load partner packages:', err);
      setError(err?.detail || t('partner.packages.loadError', 'No se pudo cargar la información de compra'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  // Tabla de REFERENCIA (PVP de WAI al cliente final): SIN coste ni margen ni
  // botón de compra — es orientación de mercado, no la compra del partner.
  const renderReferenceTable = (rows: CreditPackage[], isPlan: boolean) => (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('partner.packages.fields.name', 'Paquete')}</TableCell>
            <TableCell align="right">{t('partner.packages.fields.credits', 'Créditos')}</TableCell>
            <TableCell align="right">
              {isPlan
                ? t('partner.packages.fields.pvpMonthly', 'PVP / mes')
                : t('partner.packages.fields.pvp', 'PVP')}
            </TableCell>
            <TableCell align="right">{t('partner.packages.fields.perCredit', 'PVP / crédito')}</TableCell>
            <TableCell>{t('partner.packages.fields.whatFor', 'Para qué vale')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((pkg) => (
            <TableRow key={pkg.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>{pkg.name}</Typography>
              </TableCell>
              <TableCell align="right">{formatCredits(pkg.credits)}</TableCell>
              <TableCell align="right">{formatUsd(pkg.price_usd)} USD</TableCell>
              <TableCell align="right">{Number(pkg.price_per_credit ?? 0).toFixed(3)} USD</TableCell>
              <TableCell sx={{ maxWidth: 320 }}>
                <Typography variant="caption" color="text.secondary">
                  {pkg.what_for || '—'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <PartnerLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PartnerLayout>
    );
  }

  const plans = data?.plans ?? [];
  const extras = data?.extras ?? [];

  // Precio mayorista PLANO por crédito: lo que de verdad le cuesta comprar (el
  // mismo compre lo que compre). Viene del backend; sin él, se deriva del precio
  // de lista y su descuento (fallback tolerante).
  const wholesalePerCredit = data?.wholesale_price_per_credit !== undefined
    && data?.wholesale_price_per_credit !== null
    ? Number(data.wholesale_price_per_credit)
    : Number(data?.credit_price_usd ?? 0) * (1 - Number(data?.wholesale_discount_pct ?? 0) / 100);

  // Estado del LÍMITE DE COMPRA MENSUAL (de /partner/me): límite, comprado este
  // ciclo, hueco y renovación.
  const cap = Number(reseller?.max_wholesale_credits ?? 0);
  const hasCap = cap > 0;
  const purchasedThisCycle = Number(reseller?.wholesale_purchased_this_cycle ?? 0);
  const headroom = hasCap ? Math.max(0, cap - purchasedThisCycle) : null;
  const renewsAt = reseller?.wholesale_cycle_renews_at ?? null;
  const renewsLabel = renewsAt
    ? new Date(renewsAt).toLocaleDateString(i18n.language || 'es')
    : null;

  return (
    <PartnerLayout>
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Box mb={3}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t('partner.packages.title', 'Comprar créditos')}
            </Typography>
            <PartnerHelpPanel screen="packages" variant="icon" />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('partner.packages.subtitle', 'Compras créditos a granel a tu precio mayorista y luego los repartes entre tus clientes. El precio al que se los cobras a ellos lo decides tú.')}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* ── Cuadro de COMPRA a granel ─────────────────────────────────── */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ py: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2} color="primary.main">
              <AddShoppingCartIcon />
              <Typography variant="h6" color="text.primary" fontWeight={700}>
                {t('partner.packages.buyBoxTitle', 'Compra créditos a granel')}
              </Typography>
            </Box>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  {t('partner.packages.discount', 'Tu descuento mayorista')}
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {formatPct(data?.wholesale_discount_pct)} %
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  {t('partner.packages.wholesalePerCredit', 'Tu precio por crédito')}
                </Typography>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                  {wholesalePerCredit.toFixed(4)} USD
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('partner.packages.flatPrice', 'Plano: el mismo compres 100 o 100.000')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  {t('partner.packages.wholesaleBalance', 'Tu bolsa mayorista')}
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {formatCredits(data?.wholesale_balance)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('partner.packages.creditsUnit', 'créditos')}
                </Typography>
              </Grid>
            </Grid>

            {/* Límite de compra MENSUAL: cuánto ha comprado de su cupo este mes. */}
            {hasCap && (
              <Alert severity="info" sx={{ mt: 2.5 }}>
                {t('partner.packages.monthlyLimit', 'Límite de compra mensual: este mes has comprado {{purchased}} de {{cap}} créditos · te quedan {{headroom}}{{renews}}.', {
                  purchased: formatCredits(purchasedThisCycle),
                  cap: formatCredits(cap),
                  headroom: formatCredits(headroom ?? 0),
                  renews: renewsLabel ? ' · ' + t('partner.packages.renews', 'se renueva el {{date}}', { date: renewsLabel }) : ''
                })}
              </Alert>
            )}

            <Box display="flex" gap={1.5} mt={2.5} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<AddShoppingCartIcon />}
                onClick={() => setBuyOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                {t('partner.purchase.title', 'Comprar créditos')}
              </Button>
              {hasCap && (
                <Button
                  variant="outlined"
                  startIcon={<TrendingUpIcon />}
                  onClick={() => setLimitOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  {t('partner.limitRequest.short', 'Ampliar límite mensual')}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* ── REFERENCIA de mercado: precios de WAI al cliente final ─────── */}
        <Box mb={1.5}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('partner.packages.referenceTitle', 'Precios de WAI al cliente final')}
            </Typography>
            <Chip size="small" variant="outlined" label={t('partner.packages.referenceChip', 'Solo referencia')} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('partner.packages.referenceSubtitle', 'A cuánto vende WAI estos paquetes al cliente directo. Es orientación de mercado para que sepas a cuánto se vende — no es tu coste (tú compras a tu precio mayorista de arriba) ni tu precio de venta (ese lo decides tú).')}
          </Typography>
        </Box>

        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t('partner.packages.plansTitle', 'Planes mensuales')}
            </Typography>
            <Chip size="small" variant="outlined" label={t('partner.packages.recurring', 'Recurrente')} />
          </Box>
        </Box>
        {plans.length === 0 ? (
          <Alert severity="info" sx={{ mb: 4 }}>
            {t('partner.packages.empty', 'No hay paquetes publicados todavía.')}
          </Alert>
        ) : (
          <Box mb={4}>{renderReferenceTable(plans, true)}</Box>
        )}

        <Divider sx={{ mb: 3 }} />

        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t('partner.packages.extrasTitle', 'Packs extra')}
            </Typography>
            <Chip size="small" variant="outlined" label={t('partner.packages.oneOff', 'Puntual')} />
          </Box>
        </Box>
        {extras.length === 0 ? (
          <Alert severity="info">
            {t('partner.packages.empty', 'No hay paquetes publicados todavía.')}
          </Alert>
        ) : (
          renderReferenceTable(extras, false)
        )}

        <Alert severity="info" sx={{ mt: 4 }}>
          <AlertTitle>{t('partner.packages.howTitle', 'Cómo se traduce esto en tu operativa')}</AlertTitle>
          {t('partner.packages.howBody', 'Compras créditos a granel aquí (entran en tu bolsa a tu precio mayorista) y luego, desde la ficha de un cliente, le das un PLAN mensual (recurrente: cada renovación descuenta de tu bolsa) o una RECARGA puntual que no caduca. Cada crédito que le asignas te cuesta tu precio mayorista; lo que le facturas a él es tu decisión.')}
        </Alert>
      </Container>

      {/* Comprar a granel: el diálogo calcula el importe con tu precio plano.
          Si no cabe en tu cupo mensual, ofrece pedir ampliación. */}
      <PurchaseCreditsDialog
        open={buyOpen}
        onClose={() => setBuyOpen(false)}
        onPurchased={() => { setBuyOpen(false); refreshMe(); }}
        maxWholesaleCredits={reseller?.max_wholesale_credits ?? null}
        purchasedThisCycle={reseller?.wholesale_purchased_this_cycle ?? null}
        cycleRenewsAt={reseller?.wholesale_cycle_renews_at ?? null}
        discountPct={data?.wholesale_discount_pct ?? null}
        creditPriceUsd={data?.credit_price_usd ?? null}
        onRequestLimit={() => setLimitOpen(true)}
      />
      <LimitRequestDialog
        open={limitOpen}
        onClose={() => setLimitOpen(false)}
        onCreated={refreshMe}
        currentMax={reseller?.max_wholesale_credits ?? null}
        wholesaleBalance={reseller?.wholesale_balance ?? null}
      />
    </PartnerLayout>
  );
};

export default PartnerPackagesPage;
